/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef } from 'react';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
    // Initialize state from localStorage or default to 'light'
    const [theme, setTheme] = useState(() => {
        try {
            return localStorage.getItem('theme') || 'light';
        } catch (e) {
            console.error(e);
            return 'light';
        }
    });

    // Keep a ref to the matchMedia so we can remove listeners
    const mqlRef = useRef(null);

    useEffect(() => {
        const root = window.document.documentElement;
        const body = window.document.body;

        const applyTheme = t => {
            const prefersDark =
                window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const applied = t === 'system' ? (prefersDark ? 'dark' : 'light') : t;

            // Remove from both root (html) and body, then add the chosen class.
            root.classList.remove('light', 'dark');
            body.classList.remove('light', 'dark');

            root.classList.add(applied);
            body.classList.add(applied);
        };

        applyTheme(theme);

        // If using system, listen for changes to OS preference
        if (theme === 'system' && window.matchMedia) {
            const mql = window.matchMedia('(prefers-color-scheme: dark)');
            mqlRef.current = mql;
            const handler = () => applyTheme('system');
            // modern API
            if (mql.addEventListener) mql.addEventListener('change', handler);
            else mql.addListener(handler);

            return () => {
                if (mql.removeEventListener) mql.removeEventListener('change', handler);
                else mql.removeListener(handler);
            };
        }
    }, [theme]);

    useEffect(() => {
        try {
            localStorage.setItem('theme', theme);
        } catch (e) {
            // ignore
            console.error(e);
        }
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
    };

    const value = {
        theme,
        toggleTheme,
        setTheme,
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
